import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CATEGORIES, NEWS } from "@/mocks/news";
import NewsCard from "@/components/feature/NewsCard";
import { formatViews } from "@/utils/format";
import useSeo from "@/hooks/useSeo";

const LABELS: Record<string, { name: string; desc: string }> = {
  all: { name: "全部资讯", desc: "浏览娱乐爆料站内全部独家资讯，随时随地掌握娱乐圈最新动态。" },
  exclusive: { name: "独家爆料", desc: "娱乐爆料独家首发，第一手现场直击与未公开细节抢先看。" },
  stars: { name: "明星动态", desc: "明星动态追踪，聚焦演员与艺人的最新动向、专访与生活日常。" },
  drama: { name: "热剧追踪", desc: "热剧追踪，带你直击热门剧集片场手记与口碑复盘。" },
  music: { name: "音乐现场", desc: "音乐现场资讯，新专辑首发、演出直击与独立音乐人专访。" },
  variety: { name: "综艺圈", desc: "综艺圈观察，慢综艺、真人秀与幕后制作方法论深度解读。" },
};

export default function CategoryPage() {
  const { slug = "all" } = useParams();
  const info = LABELS[slug] || LABELS.all;
  const [query, setQuery] = useState("");

  useSeo({
    title: `${info.name} - 娱乐爆料 · 独家首发第一手资讯`,
    description: info.desc,
    keywords: `娱乐爆料,${info.name},娱乐资讯,明星动态`,
  });

  const list = useMemo(() => {
    let data = slug === "all" ? [...NEWS] : NEWS.filter((n) => n.categorySlug === slug);
    const q = query.trim().toLowerCase();
    if (q) {
      data = data.filter(
        (n) =>
          n.title.toLowerCase().includes(q) ||
          n.summary.toLowerCase().includes(q) ||
          n.tag.toLowerCase().includes(q),
      );
    }
    return data.sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [slug, query]);

  const totalViews = useMemo(() => NEWS.reduce((s, n) => s + n.views, 0), []);

  return (
    <div>
      <div className="mx-auto max-w-7xl px-4 pt-10 md:px-6">
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-foreground-400">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="font-medium text-foreground-700">{info.name}</span>
        </nav>
        <div className="mt-6 flex flex-col justify-between gap-6 border-b border-background-200 pb-8 lg:flex-row lg:items-end">
          <div>
            <h1 className="text-2xl font-black text-foreground-950 md:text-4xl">{info.name}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{info.desc}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                  slug === c.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-500">
                共 <b className="text-foreground-900">{list.length}</b> 篇资讯 · 累计
                <b className="text-foreground-900"> {formatViews(totalViews)} </b>阅读
              </p>
              <label className="relative block w-full max-w-xs">
                <span className="sr-only">搜索资讯</span>
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  type="search"
                  placeholder="搜索该栏目..."
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </label>
            </div>

            {list.length > 0 ? (
              <div className="flex flex-col gap-4">
                {list.map((item) => <NewsCard key={item.id} item={item} horizontal />)}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 py-20 text-center">
                <i className="ri-file-search-line text-4xl text-foreground-300"></i>
                <p className="mt-4 font-medium text-foreground-700">没有找到相关资讯</p>
                <p className="mt-1 text-sm text-foreground-400">换个关键词试试吧</p>
              </div>
            )}
          </div>

          {/* 侧栏 */}
          <aside className="space-y-6 lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-black text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-background-50"><i className="ri-fire-fill text-base"></i></span>
                热门排行
              </h2>
              <ol className="space-y-1">
                {[...NEWS].sort((a, b) => b.views - a.views).slice(0, 6).map((item, idx) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.slug}`} className="group flex items-start gap-3 rounded-md px-2 py-2 hover:bg-background-50">
                      <span className={`mt-0.5 w-5 shrink-0 text-center font-display text-base font-bold ${idx < 3 ? "text-primary-600" : "text-foreground-300"}`}>{idx + 1}</span>
                      <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 group-hover:text-primary-700">{item.title}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-lg bg-primary-600 p-6 text-background-50">
              <h2 className="text-lg font-black">有独家线索要爆料？</h2>
              <p className="mt-2 text-sm leading-relaxed text-background-100">
                欢迎通过邮箱联系编辑团队，我们会对线索严格保密，核实后第一时间跟进报道。
              </p>
              <a href="mailto:contact@entertainment.example.com" className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-4 py-2.5 text-sm font-bold text-primary-700 hover:bg-background-100">
                投稿爆料 <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}